"use client";

import {useGSAP} from "@gsap/react";
import {gsap} from "gsap";
import {useRef} from "react";
import {useI18n} from "@/lib/i18n/provider";
import type {UserWorkflowEntry} from "@/lib/workflows/user-entries";

gsap.registerPlugin(useGSAP);

type EntryState = "loading" | "ready" | "empty" | "error";

/** 动态工作流选择器只消费卡片安全投影；选择动画和键盘 button 语义保持不变。 */
export function NewConversationChooser({entries, state, selected, onSelect}: {entries: UserWorkflowEntry[]; state: EntryState; selected: string | null; onSelect: (workflowKey: string) => void}) {
  const {t, locale} = useI18n();
  const rootRef = useRef<HTMLElement>(null);

  const {contextSafe} = useGSAP(() => {
    const cards = gsap.utils.toArray<HTMLElement>(".new-conversation-intent", rootRef.current);
    const heading = rootRef.current?.querySelector<HTMLElement>(".new-conversation-heading");
    if (heading) gsap.fromTo(heading, {autoAlpha: 0, y: 18}, {autoAlpha: 1, y: 0, duration: 0.42, ease: "power3.out"});
    if (cards.length) gsap.fromTo(cards, {autoAlpha: 0, y: 28, scale: 0.97}, {autoAlpha: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.07, ease: "back.out(1.35)", clearProps: "opacity,visibility"});
  }, {scope: rootRef, dependencies: [entries.map((entry) => entry.workflowKey).join("|")]});

  const choose = (workflowKey: string) => {
    contextSafe(() => {
      const root = rootRef.current;
      if (!root) return;
      const cards = gsap.utils.toArray<HTMLElement>(".new-conversation-intent", root);
      const targetCard = cards.find((card) => card.dataset.conversationIntent === workflowKey);
      const detail = root.querySelector<HTMLElement>(".new-conversation-detail");
      const copies = gsap.utils.toArray<HTMLElement>(".new-conversation-detail-copy", root);
      const currentCopy = selected ? copies.find((copy) => copy.dataset.intentCopy === selected) ?? null : null;
      const targetCopy = copies.find((copy) => copy.dataset.intentCopy === workflowKey);
      if (!targetCard || !detail || !targetCopy) return;
      gsap.killTweensOf([...cards, detail, ...gsap.utils.toArray<HTMLElement>(".new-conversation-detail-copy", root)]);
      onSelect(workflowKey);
      // 手机端卡片纵向紧邻，放大并上移会遮住前一项；仅桌面保留浮起效果。
      const isMobile = typeof window !== "undefined" && window.innerWidth <= 760;
      const timeline = gsap.timeline({defaults: {ease: "power3.out"}});
      timeline.to(cards, {y: isMobile ? 0 : 4, scale: isMobile ? 1 : 0.985, autoAlpha: 0.62, duration: 0.34}, 0)
        .to(targetCard, {y: isMobile ? 0 : -14, scale: isMobile ? 1 : 1.035, autoAlpha: 1, duration: 0.46, ease: "back.out(1.7)"}, 0);
      if (!selected) timeline.set(detail, {autoAlpha: 1}, 0.08).fromTo(detail, {y: 16, scaleY: 0.9}, {y: 0, scaleY: 1, duration: 0.38, transformOrigin: "top center"}, 0.08);
      if (currentCopy && currentCopy !== targetCopy) timeline.to(currentCopy, {autoAlpha: 0, y: -8, duration: 0.16, ease: "power2.in"}, 0);
      timeline.fromTo(targetCopy, {autoAlpha: 0, y: 14}, {autoAlpha: 1, y: 0, duration: 0.34}, selected ? 0.13 : 0.16);
    })();
  };

  const statusText = state === "error"
    ? (locale === "en" ? "Workflow entries are temporarily unavailable." : locale === "zh-TW" ? "工作流程入口暫時無法使用。" : "工作流入口暂时不可用。")
    : (locale === "en" ? "No authorized workflows are available." : locale === "zh-TW" ? "目前沒有可用的已授權工作流程。" : "当前没有可用的已授权工作流。");

  return <section ref={rootRef} className="new-conversation-stage" aria-labelledby="new-conversation-title">
    <header className="new-conversation-heading"><span>{t("chat.newConversationEyebrow")}</span><h2 id="new-conversation-title">{t("chat.newConversationTitle")}</h2><p>{t("chat.newConversationCopy")}</p></header>
    {state === "ready" ? <>
      <div className="new-conversation-intents" data-entry-count={entries.length} role="group" aria-label={t("chat.chooseDomain")}>
        {entries.map((entry, index) => <button key={entry.workflowKey} className={`new-conversation-intent ${selected === entry.workflowKey ? "is-selected" : ""}`} type="button" data-conversation-intent={entry.workflowKey} aria-pressed={selected === entry.workflowKey} aria-describedby={selected === entry.workflowKey ? `intent-description-${entry.workflowKey}` : undefined} onClick={() => choose(entry.workflowKey)}>
          <span className="new-conversation-intent-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><strong>{entry.title[locale]}</strong><span className="new-conversation-intent-arrow" aria-hidden="true">↗</span>
        </button>)}
      </div>
      <div className="new-conversation-detail" aria-live="polite" aria-atomic="true">{entries.map((entry) => <p key={entry.workflowKey} id={`intent-description-${entry.workflowKey}`} className="new-conversation-detail-copy" data-intent-copy={entry.workflowKey} aria-hidden={selected !== entry.workflowKey}>{entry.description[locale]}</p>)}</div>
    </> : state === "loading" ? null : <p className="new-conversation-entry-state" role={state === "error" ? "alert" : "status"}>{statusText}</p>}
  </section>;
}
